import React from "react";
import { PageTab } from "../../atoms/PageTab";
import { SoonBadge } from "../../atoms/SoonBadge";
import { RowActionMenu } from "../RowActionMenu";
import { AddButton, Bar, EditCell, EditInput, MenuCell, Meta, Spacer } from "./PageTabBar.styles";
import type { PageTabBarProps } from "./PageTabBar.types";

/**
 * Numbered page tabs along the foot of a page, an add button and meta text on the right.
 * Stateless. Not an extension of CategoryTabStrip: that one is underlined tablist tabs;
 * this one is numbered, divided, aria-current navigation with an add slot and meta text.
 */
export const PageTabBar: React.FC<PageTabBarProps> = ({
  ariaLabel,
  pages,
  activeKey,
  onSelect,
  meta,
  addLabel,
  onAdd,
  addDisabled = false,
  addDisabledReason,
  tabTitle,
  onTabDoubleClick,
  editingKey = null,
  editLabel,
  onEditCommit,
  onEditCancel,
  activeTabMenu,
  className,
}) => (
  <Bar aria-label={ariaLabel} className={className}>
    {pages.map((page, index) => {
      const isActive = page.key === activeKey;
      return (
        <React.Fragment key={page.key}>
          {page.key === editingKey ? (
            <EditCell>
              <EditInput
                autoFocus
                aria-label={editLabel ?? page.label}
                defaultValue={page.label}
                maxLength={80}
                onFocus={(event) => event.currentTarget.select()}
                onBlur={(event) => onEditCommit?.(page.key, event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") event.currentTarget.blur();
                  if (event.key === "Escape") {
                    event.currentTarget.value = page.label;
                    onEditCancel?.();
                  }
                }}
              />
            </EditCell>
          ) : (
            <PageTab
              label={page.label}
              number={index + 1}
              isActive={isActive}
              title={onTabDoubleClick ? tabTitle : undefined}
              onClick={() => onSelect(page.key)}
              onDoubleClick={onTabDoubleClick ? () => onTabDoubleClick(page.key) : undefined}
            />
          )}
          {isActive && activeTabMenu && activeTabMenu.items.length > 0 ? (
            <MenuCell>
              <RowActionMenu aria-label={activeTabMenu.ariaLabel} items={activeTabMenu.items} />
            </MenuCell>
          ) : null}
        </React.Fragment>
      );
    })}
    {addLabel ? (
      <AddButton
        type="button"
        aria-disabled={addDisabled || undefined}
        title={addDisabled ? addDisabledReason : undefined}
        aria-label={addDisabled && addDisabledReason ? `${addLabel}, ${addDisabledReason.toLowerCase()}` : undefined}
        onClick={addDisabled ? undefined : onAdd}
      >
        {addLabel}
        {addDisabled && addDisabledReason ? <span aria-hidden><SoonBadge label={addDisabledReason} /></span> : null}
      </AddButton>
    ) : null}
    <Spacer />
    {meta ? <Meta>{meta}</Meta> : null}
  </Bar>
);

export default PageTabBar;
